import { Box, Grid, Popover } from '@mui/material';
import Button from '@mui/material/Button';
import { Theme, useTheme } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import Loader, { LoadingStage } from 'components/Loader';
import AdvancedSearchDrawer from 'components/SearchFilters/AdvancedSearch';
import DashboardFooter from 'components/StudiesDashboard/DashboardFooter';
import { JobArchiveState, JobType } from 'interfaces/job';
import { Permission } from 'interfaces/permissionOption';
import { flatMap, includes, isEmpty, omitBy, pick } from 'lodash';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BooleanParam, StringParam, useQueryParam } from 'use-query-params';
import { useJobs } from 'utils/queryHooks/jobs/useJobs';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { usePermissions } from 'utils/usePermissions';
import { useProceduresWithQAExperimentResultsOnly } from 'utils/useProceduresWithQAExperimentResultsOnly';

import { getCohort } from 'api/cohorts';
import {
  Main,
  studyDashboardHeaderHeight,
  studyDashboardTabsHeight,
} from 'components/atoms/FlexContainers/FlexContainers';
import EditDrawer from 'components/EditDrawer';
import { pageHeaderHeight } from 'components/PageHeader';
import { EmbeddedJobsView } from 'components/Pages/Jobs';
import CellRulePanelsContainer from 'components/Pages/OperationsSettings/CellRulesDataGrid/CellRulePanelsContainer';
import useMainFilters from 'components/SearchFilters/hooks/useMainFilters';
import { SITE_HEADER_NUMBER } from 'components/SiteHeader/SiteHeader';
import CellNeighborhoods from 'components/StudyDashboard/Tabs/CellNeighborhoods';
import MifMarkerUnsupervisedAnalysis from 'components/StudyDashboard/Tabs/MifMarkerUnsupervisedAnalysis';
import QaAnalysis from 'components/StudyDashboard/Tabs/QaAnalysis';
import { reportTabJobStatuses } from 'components/StudyDashboard/Tabs/reportTabJobs';
import StudyReport from 'components/StudyDashboard/Tabs/StudyReport';
import { getDashboardCanvasStyles, getTranslucentSurfaceStyles } from 'components/theme/theme';
import { Cohort } from 'interfaces/cohort';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import { useStudyStainTypeIds } from 'utils/queryHooks/stainType/useStudyStainTypes';
import useStudy from 'utils/useStudy';
import CohortDetails from './Cohort/CohortDetails';
import {
  DeferredStudyDashboardTabRequirementState,
  getDeferredStudyDashboardTabDecision,
  getRnaOnlyStudySetupRequirementState,
  getStudyFitDeferredStudyDashboardTabDecision,
  getStudyFitOrDataDeferredStudyDashboardTabDecision,
  shouldShowRnaIncompatibleStudyDashboardTabs,
  shouldShowStudySpecificMultiplexTabs,
} from './getStudyDashboardTabs';
import MainContent from './MainStudyDashboardContent';
import { useCasesViewMode } from './ProceduresPage/useCasesViewMode';
import QCMarkers from './QCMarkers';
import StudyDashboardHeader, { compactStudyDashboardHeaderHeight } from './StudyDashboardHeader';
import StudyDashboardTabs from './StudyDashboardTabs';
import StudyDashboardWorkflowView from './StudyDashboardWorkflowView';
import AnnotationAssignmentsView from './Tasks/AnnotationAssignmentsView';
import {
  AnnotationAssignmentsTab,
  CellNeighborhoodsTab,
  CellRulesTab,
  DatasetTab,
  JobsTab,
  MifMarkerUnsupervisedAnalysisTab,
  QaAnalysisTab,
  QCMarkersTab,
  StudyDashboardTabOptions,
  StudyReportTab,
  useStudyDashboardTabState,
  WorkflowTab,
} from './useStudyDashboardTabState';

export const studyDashboardChromeTransitionDurationMs = 120;
export const createStudyDashboardChromeTransition = (theme: Theme, properties: string[]) =>
  theme.transitions.create(properties, {
    duration: studyDashboardChromeTransitionDurationMs,
  });

const StudyDashboardTabPanel: React.FC<
  React.PropsWithChildren<{
    isActive: boolean;
    shouldMount: boolean;
  }>
> = ({ children, isActive, shouldMount }) => {
  if (!shouldMount) {
    return null;
  }

  return (
    <Box
      role="tabpanel"
      hidden={!isActive}
      aria-hidden={!isActive}
      sx={{ display: isActive ? 'block' : 'none', width: '100%', minHeight: '100%' }}
    >
      {children}
    </Box>
  );
};

export const StudyDashboardBody: React.FunctionComponent<React.PropsWithChildren<unknown>> = () => {
  const theme = useTheme();
  const { hasPermission, isLoading: isLoadingPermissions } = usePermissions();
  const lastDatasetScrollTopRef = useRef(0);
  const [advancedSearchDrawerOpen] = useQueryParam('advancedSearchDrawerOpen', BooleanParam);
  const [requestedStudyReportJobId] = useQueryParam('jobIdForStudyReportReportTab', StringParam);
  const [isDatasetHeaderCollapsed, setIsDatasetHeaderCollapsed] = useState(false);
  const [mountedTabs, setMountedTabs] = useState<Partial<Record<StudyDashboardTabOptions, boolean>>>({
    [DatasetTab]: true,
  });

  const { labId } = useCurrentLabId();

  const canViewAnnotationAssignmentsDashboard = hasPermission(Permission.ViewAnnotationsList);
  const hasPermissionToEditCellRules = hasPermission(Permission.EditCellRules);
  const hasPermissionToViewStudyCellRules = hasPermission(Permission.ViewStudyCellRules);
  const hasPermissionToViewQCMarkers = hasPermission(Permission.ViewQCMarkers);
  const hasPermissionToEditQCMarkers = hasPermission(Permission.EditQCMarkers);

  const [activeTab] = useStudyDashboardTabState();
  const { casesViewMode } = useCasesViewMode();
  const { queryParams, setQueryParams } = useEncodedFilters();
  const studyId = queryParams.filters?.studyId;

  const { isStudyIdSpecific } = useMainFilters();

  const specificStudySelected = isStudyIdSpecific(studyId);

  const { study: currentStudy, isLoading: isLoadingStudy } = useStudy(studyId, { enabled: specificStudySelected });

  const cohortMode = Boolean(queryParams.cohortId);

  const { data: proceduresData } = useProceduresWithQAExperimentResultsOnly({
    enabled: Boolean(cohortMode),
    loadIds: true,
  });

  const { data: currentCohort } = useQuery<Cohort>({
    queryKey: ['cohort', queryParams.cohortId],
    queryFn: () => getCohort(queryParams.cohortId),
    enabled: cohortMode,
  });

  const [slidesMode] = useQueryParam('slidesMode', BooleanParam);

  const [showCohortDetails, setShowCohortDetails] = useState(false);

  const [, setIsUploadSlidesModalOpen] = useQueryParam('isUploadSlidesModalOpen', BooleanParam);
  const isAnalyzing = Boolean(currentStudy?.analysisStartTime);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const { data: stainTypesInStudy, isLoading: isLoadingStudyStainTypes } = useStudyStainTypeIds(studyId, {
    enabled: specificStudySelected,
  });
  const isMultiplex = includes(stainTypesInStudy, MULTIPLEX_STAIN_ID);
  const canShowStudySpecificMultiplexTabs = shouldShowStudySpecificMultiplexTabs({
    specificStudySelected,
    isMultiplex,
  });
  const rnaOnlyStudySetupRequirementState = getRnaOnlyStudySetupRequirementState({
    isLoadingStudy,
    specificStudySelected,
    workflowModules: currentStudy?.settings?.workflow?.modules,
  });
  const canShowRnaIncompatibleStudyDashboardTabs = shouldShowRnaIncompatibleStudyDashboardTabs(
    rnaOnlyStudySetupRequirementState
  );
  const rnaOnlyStudySetupRequirement = {
    description: 'RNA-only study setup',
    kind: 'study-fit' as const,
    state: rnaOnlyStudySetupRequirementState,
  };
  const studyFitRequirementState: DeferredStudyDashboardTabRequirementState =
    specificStudySelected && isLoadingStudyStainTypes
      ? 'pending'
      : canShowStudySpecificMultiplexTabs
      ? 'available'
      : 'unavailable';

  const workflowMonitorPermissionRequirementState: DeferredStudyDashboardTabRequirementState = isLoadingPermissions
    ? 'pending'
    : hasPermission(Permission.ViewWorkflowMonitor)
    ? 'available'
    : 'unavailable';
  const workflowStudyRequirementState: DeferredStudyDashboardTabRequirementState = specificStudySelected
    ? 'available'
    : 'unavailable';
  const workflowTabDecision = getDeferredStudyDashboardTabDecision({
    activeTab,
    tab: WorkflowTab,
    requirements: [
      {
        description: 'workflow monitor permission',
        kind: 'permission',
        state: workflowMonitorPermissionRequirementState,
      },
      {
        description: 'specific study selected',
        kind: 'study-fit',
        state: workflowStudyRequirementState,
      },
    ],
  });

  const qaAnalysisPermissionRequirementState: DeferredStudyDashboardTabRequirementState = isLoadingPermissions
    ? 'pending'
    : hasPermission(Permission.ViewQaAnalysisResults)
    ? 'available'
    : 'unavailable';
  const qaAnalysisTabDecision = getStudyFitDeferredStudyDashboardTabDecision({
    activeTab,
    permissionRequirement: {
      description: 'QA Analysis permission',
      kind: 'permission',
      state: qaAnalysisPermissionRequirementState,
    },
    studyFitRequirement: {
      description: 'mIF study fit',
      kind: 'study-fit',
      state: studyFitRequirementState,
    },
    additionalRequirements: [rnaOnlyStudySetupRequirement],
    tab: QaAnalysisTab,
  });

  const mifMarkerUnsupervisedAnalysisPermissionRequirementState: DeferredStudyDashboardTabRequirementState =
    isLoadingPermissions
      ? 'pending'
      : hasPermission(Permission.ViewMifMarkerUnsupervisedAnalysisResults)
      ? 'available'
      : 'unavailable';
  const mifMarkerUnsupervisedAnalysisTabDecision = getStudyFitDeferredStudyDashboardTabDecision({
    activeTab,
    permissionRequirement: {
      description: 'mIF unsupervised analysis permission',
      kind: 'permission',
      state: mifMarkerUnsupervisedAnalysisPermissionRequirementState,
    },
    studyFitRequirement: {
      description: 'mIF study fit',
      kind: 'study-fit',
      state: studyFitRequirementState,
    },
    additionalRequirements: [rnaOnlyStudySetupRequirement],
    tab: MifMarkerUnsupervisedAnalysisTab,
  });

  const studyReportPermissionRequirementState: DeferredStudyDashboardTabRequirementState = isLoadingPermissions
    ? 'pending'
    : hasPermission(Permission.ViewStudyReportResults)
    ? 'available'
    : 'unavailable';
  const canQueryStudyReportJobs = studyReportPermissionRequirementState === 'available';
  const { data: studyReportJobsResponse, isLoading: isLoadingStudyReportJobs } = useJobs({
    page: 1,
    pageSize: 1,
    enabled: canQueryStudyReportJobs && Boolean(studyId),
    noInitialData: true,
    keepPreviousData: false,
    additionalFilters: {
      archiveState: JobArchiveState.Active,
      types: [JobType.StudyReport],
      statuses: [...reportTabJobStatuses],
      studyId,
    },
  });
  const hasStudyReportJobs = Boolean(requestedStudyReportJobId) || (studyReportJobsResponse?.totalJobs ?? 0) > 0;
  const studyReportDataRequirementState: DeferredStudyDashboardTabRequirementState = canQueryStudyReportJobs
    ? isLoadingStudyReportJobs
      ? 'pending'
      : hasStudyReportJobs
      ? 'available'
      : 'unavailable'
    : studyReportPermissionRequirementState === 'pending'
    ? 'pending'
    : 'unavailable';
  const studyReportTabDecision = getDeferredStudyDashboardTabDecision({
    activeTab,
    tab: StudyReportTab,
    // Study Report is intentionally not limited to mIF studies.
    requirements: [
      {
        description: 'Study Report permission',
        kind: 'permission',
        state: studyReportPermissionRequirementState,
      },
      {
        description: 'Study Report jobs',
        kind: 'data',
        state: studyReportDataRequirementState,
      },
    ],
  });

  const cellNeighborhoodsPermissionRequirementState: DeferredStudyDashboardTabRequirementState = isLoadingPermissions
    ? 'pending'
    : hasPermission(Permission.ViewJobs)
    ? 'available'
    : 'unavailable';
  const canQueryCellNeighborhoodJobs =
    cellNeighborhoodsPermissionRequirementState === 'available' && studyFitRequirementState === 'unavailable';
  const { data: cellNeighborhoodJobsResponse, isLoading: isLoadingCellNeighborhoodJobs } = useJobs({
    page: 1,
    pageSize: 1,
    enabled: canQueryCellNeighborhoodJobs && Boolean(studyId),
    noInitialData: true,
    keepPreviousData: false,
    additionalFilters: {
      types: [JobType.MultiplexNeighborhoodClusters, JobType.NeighborhoodClusters],
      statuses: [...reportTabJobStatuses],
      studyId,
    },
  });
  const hasCellNeighborhoodJobs = (cellNeighborhoodJobsResponse?.totalJobs ?? 0) > 0;
  const cellNeighborhoodsDataRequirementState: DeferredStudyDashboardTabRequirementState = canQueryCellNeighborhoodJobs
    ? isLoadingCellNeighborhoodJobs
      ? 'pending'
      : hasCellNeighborhoodJobs
      ? 'available'
      : 'unavailable'
    : cellNeighborhoodsPermissionRequirementState === 'pending'
    ? 'pending'
    : 'unavailable';
  const cellNeighborhoodsTabDecision = getStudyFitOrDataDeferredStudyDashboardTabDecision({
    activeTab,
    dataRequirement: {
      description: 'Cell Neighborhoods jobs',
      kind: 'data',
      state: cellNeighborhoodsDataRequirementState,
    },
    permissionRequirement: {
      description: 'Jobs permission',
      kind: 'permission',
      state: cellNeighborhoodsPermissionRequirementState,
    },
    studyFitRequirement: {
      description: 'mIF study fit',
      kind: 'study-fit',
      state: studyFitRequirementState,
    },
    additionalRequirements: [rnaOnlyStudySetupRequirement],
    tab: CellNeighborhoodsTab,
  });

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>) => {
    setShowCohortDetails(true);
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setShowCohortDetails(false);
    setAnchorEl(null);
  };

  const applyCohortFilters = () => {
    setQueryParams({
      ...omitBy(
        pick(currentCohort.queryObject, ['filters', 'features', 'clinicalData', 'compositeFilters', 'searchTerm']),
        isEmpty
      ),
      cohortId: null,
      cohortName: null,
      labId,
    });

    setShowCohortDetails(false);
  };

  const insideCohort = cohortMode && Boolean(currentCohort);

  let pageTitle = '';
  if (!insideCohort) {
    pageTitle = currentStudy?.name || 'Back to Studies';
  } else {
    pageTitle = currentCohort?.casesSnapshot ? `Cohort ${currentCohort?.name}` : `Query ${currentCohort?.name}`;
  }

  const isAddSlidesDisabled = !currentStudy?.name || isAnalyzing;

  const borderColor = theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800];

  const slides = flatMap(proceduresData?.procedures, (procedure) => procedure.slides);

  const renderCohortDetailsToggle = () => {
    return currentCohort && currentCohort?.casesSnapshot ? (
      <Grid item sx={{ pl: 0, display: 'flex' }}>
        <Button onClick={handleOpenPopover} color="secondary" size="small">
          {showCohortDetails ? 'Hide Details' : 'Show Details'}
        </Button>

        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={handleClosePopover}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <CohortDetails cohort={currentCohort} applyFunction={applyCohortFilters} />
        </Popover>
      </Grid>
    ) : null;
  };

  const showApprovedFilter = hasPermission(Permission.ApproveCases);

  const showAnnotationAssignments = activeTab === AnnotationAssignmentsTab && canViewAnnotationAssignmentsDashboard;
  const showJobs = activeTab === JobsTab && hasPermission(Permission.ViewJobs);
  const showCellRules =
    activeTab === CellRulesTab &&
    (hasPermissionToEditCellRules || hasPermissionToViewStudyCellRules) &&
    canShowStudySpecificMultiplexTabs &&
    canShowRnaIncompatibleStudyDashboardTabs;
  const showQCMarkers =
    activeTab === QCMarkersTab &&
    (hasPermissionToViewQCMarkers || hasPermissionToEditQCMarkers) &&
    canShowStudySpecificMultiplexTabs;
  const showWorkflow = activeTab === WorkflowTab && workflowTabDecision.shouldShowTab;
  const showWorkflowContent = showWorkflow && workflowTabDecision.shouldRenderContent;
  const showQaAnalysis = activeTab === QaAnalysisTab && qaAnalysisTabDecision.shouldShowTab;
  const showMifMarkerUnsupervisedAnalysis =
    activeTab === MifMarkerUnsupervisedAnalysisTab && mifMarkerUnsupervisedAnalysisTabDecision.shouldShowTab;
  const showStudyReport = activeTab === StudyReportTab && studyReportTabDecision.shouldShowTab;
  const showCellNeighborhoods = activeTab === CellNeighborhoodsTab && cellNeighborhoodsTabDecision.shouldShowTab;
  const showDatasetContent = !(
    showAnnotationAssignments ||
    showJobs ||
    showCellRules ||
    showQCMarkers ||
    showWorkflow ||
    showQaAnalysis ||
    showMifMarkerUnsupervisedAnalysis ||
    showCellNeighborhoods ||
    showStudyReport
  );
  const activeDashboardTab: StudyDashboardTabOptions = showAnnotationAssignments
    ? AnnotationAssignmentsTab
    : showJobs
    ? JobsTab
    : showCellRules
    ? CellRulesTab
    : showQCMarkers
    ? QCMarkersTab
    : showWorkflow
    ? WorkflowTab
    : showQaAnalysis
    ? QaAnalysisTab
    : showMifMarkerUnsupervisedAnalysis
    ? MifMarkerUnsupervisedAnalysisTab
    : showCellNeighborhoods
    ? CellNeighborhoodsTab
    : showStudyReport
    ? StudyReportTab
    : DatasetTab;
  const shouldCollapseDatasetHeader = activeDashboardTab === DatasetTab && casesViewMode !== 'table';
  const shouldMountDatasetContent = showDatasetContent || Boolean(mountedTabs[DatasetTab]);
  const shouldMountAnnotationAssignments =
    canViewAnnotationAssignmentsDashboard &&
    (showAnnotationAssignments || Boolean(mountedTabs[AnnotationAssignmentsTab]));
  const shouldMountJobs = hasPermission(Permission.ViewJobs) && (showJobs || Boolean(mountedTabs[JobsTab]));
  const shouldMountCellRules =
    canShowStudySpecificMultiplexTabs &&
    canShowRnaIncompatibleStudyDashboardTabs &&
    (hasPermissionToEditCellRules || hasPermissionToViewStudyCellRules) &&
    (showCellRules || Boolean(mountedTabs[CellRulesTab]));
  const shouldMountQCMarkers =
    canShowStudySpecificMultiplexTabs &&
    (hasPermissionToViewQCMarkers || hasPermissionToEditQCMarkers) &&
    (showQCMarkers || Boolean(mountedTabs[QCMarkersTab]));
  const shouldMountWorkflow =
    workflowTabDecision.shouldShowTab &&
    (showDatasetContent || showWorkflow || Boolean(mountedTabs[DatasetTab]) || Boolean(mountedTabs[WorkflowTab]));
  const shouldMountQaAnalysis =
    qaAnalysisTabDecision.shouldShowTab && (showQaAnalysis || Boolean(mountedTabs[QaAnalysisTab]));
  const shouldMountMifMarkerUnsupervisedAnalysis =
    mifMarkerUnsupervisedAnalysisTabDecision.shouldShowTab &&
    (showMifMarkerUnsupervisedAnalysis || Boolean(mountedTabs[MifMarkerUnsupervisedAnalysisTab]));
  const shouldMountCellNeighborhoods =
    cellNeighborhoodsTabDecision.shouldShowTab && (showCellNeighborhoods || Boolean(mountedTabs[CellNeighborhoodsTab]));
  const shouldMountStudyReport =
    studyReportTabDecision.shouldShowTab && (showStudyReport || Boolean(mountedTabs[StudyReportTab]));
  // The workflow projection feeds both the visible Workflow tab and the compact Dataset status.
  // Once its requirements are available, keep that projection rendered inside the hidden tab panel
  // so switching back to Dataset does not unmount it and reset the shared overview to loading.
  const shouldRenderWorkflowContent =
    shouldMountWorkflow &&
    (workflowTabDecision.availability === 'available' || workflowTabDecision.shouldRenderContent);
  const shouldRenderQaAnalysisContent = qaAnalysisTabDecision.shouldRenderContent && shouldMountQaAnalysis;
  const shouldRenderMifMarkerUnsupervisedAnalysisContent =
    mifMarkerUnsupervisedAnalysisTabDecision.shouldRenderContent && shouldMountMifMarkerUnsupervisedAnalysis;
  const shouldRenderStudyReportContent = studyReportTabDecision.shouldRenderContent && shouldMountStudyReport;
  const shouldRenderCellNeighborhoodsContent =
    cellNeighborhoodsTabDecision.shouldRenderContent && shouldMountCellNeighborhoods;
  const showPersistentStudyFilters = showDatasetContent || showWorkflowContent;

  useEffect(() => {
    setMountedTabs((currentMountedTabs) => {
      if (currentMountedTabs[activeDashboardTab]) {
        return currentMountedTabs;
      }

      return {
        ...currentMountedTabs,
        [activeDashboardTab]: true,
      };
    });
  }, [activeDashboardTab]);

  useEffect(() => {
    if (!shouldCollapseDatasetHeader) {
      setIsDatasetHeaderCollapsed(false);
      lastDatasetScrollTopRef.current = 0;
    }
  }, [shouldCollapseDatasetHeader]);

  const handleDatasetScroll = useCallback(
    (nextScrollTop: number) => {
      const expandResetThreshold = 72;
      const collapseStartThreshold = 120;
      const collapseDirectionThreshold = 24;
      const expandDirectionThreshold = 40;

      if (!shouldCollapseDatasetHeader) {
        setIsDatasetHeaderCollapsed(false);
        lastDatasetScrollTopRef.current = nextScrollTop;
        return;
      }

      if (nextScrollTop <= expandResetThreshold) {
        setIsDatasetHeaderCollapsed(false);
        lastDatasetScrollTopRef.current = nextScrollTop;
        return;
      }

      const scrollDelta = nextScrollTop - lastDatasetScrollTopRef.current;

      if (
        !isDatasetHeaderCollapsed &&
        nextScrollTop >= collapseStartThreshold &&
        scrollDelta >= collapseDirectionThreshold
      ) {
        setIsDatasetHeaderCollapsed(true);
      } else if (isDatasetHeaderCollapsed && scrollDelta <= -expandDirectionThreshold) {
        setIsDatasetHeaderCollapsed(false);
      }

      lastDatasetScrollTopRef.current = nextScrollTop;
    },
    [isDatasetHeaderCollapsed, shouldCollapseDatasetHeader]
  );

  const isStudyHeaderCollapsed = shouldCollapseDatasetHeader && isDatasetHeaderCollapsed;
  const currentStudyHeaderHeight = isStudyHeaderCollapsed
    ? compactStudyDashboardHeaderHeight
    : studyDashboardHeaderHeight;
  const stickyChromeHeight = currentStudyHeaderHeight + studyDashboardTabsHeight;
  const stickyChromeOffset = stickyChromeHeight ? `${stickyChromeHeight}px` : 0;

  return (
    <>
      <Grid
        container
        direction={'column'}
        sx={{
          height: `calc(100vh - ${pageHeaderHeight}px)`,
          width: '100%',
          ...getDashboardCanvasStyles(theme),
          marginTop: `${pageHeaderHeight}px`,
        }}
      >
        <Grid
          item
          container
          sx={{
            width: '100%',
            position: 'sticky',
            top: SITE_HEADER_NUMBER,
            zIndex: theme.zIndex.appBar - 1,
            ...getTranslucentSurfaceStyles(theme),
            borderBottom: `1px solid ${borderColor}`,
            marginBottom: stickyChromeHeight ? `-${stickyChromeOffset}` : 0,
            transition: createStudyDashboardChromeTransition(theme, ['margin-bottom']),
          }}
        >
          <Box
            data-testid="study-dashboard-header-collapse-region"
            data-collapsed={isStudyHeaderCollapsed ? 'true' : 'false'}
            sx={{
              width: '100%',
              overflow: 'hidden',
              height: `${currentStudyHeaderHeight}px`,
              transition: createStudyDashboardChromeTransition(theme, ['height']),
            }}
          >
            <StudyDashboardHeader
              compact={isStudyHeaderCollapsed}
              study={currentStudy}
              cohortMode={cohortMode}
              renderCohortDetailsToggle={renderCohortDetailsToggle}
              pageTitle={pageTitle}
              tab={queryParams.cohortId ? 'cohorts' : 'studies'}
              tooltipText={
                currentStudy?.name
                  ? isAnalyzing
                    ? 'Upload slides is already done'
                    : 'Upload slides'
                  : 'You need to choose Study'
              }
              isAddSlidesDisabled={isAddSlidesDisabled}
              onAddSlidesClick={() => {
                setIsUploadSlidesModalOpen(true);
              }}
            />
          </Box>
          <StudyDashboardTabs studyId={studyId} />
        </Grid>
        <Grid
          item
          container
          xs={true}
          data-testid="study-dashboard-scroll-region"
          onScroll={(event) => {
            handleDatasetScroll(event.currentTarget.scrollTop);
          }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
            width: '100%',
            minHeight: 0,
            paddingTop: stickyChromeOffset,
            transition: createStudyDashboardChromeTransition(theme, ['padding-top']),
          }}
        >
          <Grid
            item
            container
            xs={true}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              width: '100%',
              minHeight: '100%',
            }}
          >
            <StudyDashboardTabPanel isActive={showDatasetContent} shouldMount={shouldMountDatasetContent}>
              <MainContent />
            </StudyDashboardTabPanel>
            <StudyDashboardTabPanel isActive={showAnnotationAssignments} shouldMount={shouldMountAnnotationAssignments}>
              <AnnotationAssignmentsView studyId={studyId} />
            </StudyDashboardTabPanel>
            <StudyDashboardTabPanel isActive={showJobs} shouldMount={shouldMountJobs}>
              <EmbeddedJobsView />
            </StudyDashboardTabPanel>
            <StudyDashboardTabPanel isActive={showCellRules} shouldMount={shouldMountCellRules}>
              <Grid
                container
                sx={{
                  display: 'block',
                  pt: 3,
                  px: 10,
                }}
              >
                <CellRulePanelsContainer studyId={studyId} editable={hasPermissionToEditCellRules} />
              </Grid>
            </StudyDashboardTabPanel>
            <StudyDashboardTabPanel isActive={showQCMarkers} shouldMount={shouldMountQCMarkers}>
              <QCMarkers studyId={studyId} />
            </StudyDashboardTabPanel>
            <StudyDashboardTabPanel isActive={showWorkflow} shouldMount={shouldMountWorkflow}>
              {shouldRenderWorkflowContent ? (
                <StudyDashboardWorkflowView
                  study={currentStudy}
                  isMultiplexStudy={isMultiplex}
                  advancedSearchDrawerOpen={advancedSearchDrawerOpen}
                />
              ) : (
                <Loader stage={LoadingStage.LoadingReport} />
              )}
            </StudyDashboardTabPanel>
            <StudyDashboardTabPanel isActive={showQaAnalysis} shouldMount={shouldMountQaAnalysis}>
              {shouldRenderQaAnalysisContent ? (
                <QaAnalysis selectedStudyId={studyId} />
              ) : (
                <Loader stage={LoadingStage.LoadingReport} />
              )}
            </StudyDashboardTabPanel>
            <StudyDashboardTabPanel
              isActive={showMifMarkerUnsupervisedAnalysis}
              shouldMount={shouldMountMifMarkerUnsupervisedAnalysis}
            >
              {shouldRenderMifMarkerUnsupervisedAnalysisContent ? (
                <MifMarkerUnsupervisedAnalysis selectedStudyId={studyId} />
              ) : (
                <Loader stage={LoadingStage.LoadingReport} />
              )}
            </StudyDashboardTabPanel>
            <StudyDashboardTabPanel isActive={showCellNeighborhoods} shouldMount={shouldMountCellNeighborhoods}>
              {shouldRenderCellNeighborhoodsContent ? (
                <CellNeighborhoods selectedStudyId={studyId} />
              ) : (
                <Loader stage={LoadingStage.LoadingReport} />
              )}
            </StudyDashboardTabPanel>
            <StudyDashboardTabPanel isActive={showStudyReport} shouldMount={shouldMountStudyReport}>
              {shouldRenderStudyReportContent ? (
                <StudyReport selectedStudyId={studyId} />
              ) : (
                <Loader stage={LoadingStage.LoadingReport} />
              )}
            </StudyDashboardTabPanel>
          </Grid>
        </Grid>
        <Main leftDrawerOpen={advancedSearchDrawerOpen}>
          <DashboardFooter />
        </Main>
      </Grid>
      {showPersistentStudyFilters && (
        <>
          <AdvancedSearchDrawer
            showApprovedFilter={showApprovedFilter}
            persistent
            reserveBottomDrawerSpace={showDatasetContent}
          />
          {showDatasetContent && hasPermission(Permission.ViewCasesTable) && (
            <EditDrawer totalItemsToEdit={slidesMode ? slides?.length : proceduresData?.totalProcedures} />
          )}
        </>
      )}
    </>
  );
};

export default StudyDashboardBody;
